import { useTranslation } from 'react-i18next'
import {
  FireIcon,
  BoltIcon,
  HeartIcon,
  ScaleIcon,
  StarIcon,
} from '@heroicons/react/24/outline'

const sectionColors = {
  nutrition: { bg: 'bg-green-50', border: 'border-green-200', icon: 'text-green-600', header: 'bg-green-100' },
  exercise: { bg: 'bg-blue-50', border: 'border-blue-200', icon: 'text-blue-600', header: 'bg-blue-100' },
  activity: { bg: 'bg-orange-50', border: 'border-orange-200', icon: 'text-orange-600', header: 'bg-orange-100' },
  body: { bg: 'bg-purple-50', border: 'border-purple-200', icon: 'text-purple-600', header: 'bg-purple-100' },
}

function MetricCard({ label, value, unit, subtext }) {
  return (
    <div className="text-center p-3">
      <p className="text-sm text-gray-500">{label}</p>
      <p className="text-2xl font-bold text-gray-900">
        {value !== null && value !== undefined ? value : '--'}
        {unit && <span className="text-sm font-normal text-gray-500 ml-1">{unit}</span>}
      </p>
      {subtext && <p className="text-xs text-gray-400 mt-1">{subtext}</p>}
    </div>
  )
}

function formatChange(value) {
  if (value === null || value === undefined) return null
  const sign = value > 0 ? '+' : ''
  return `${sign}${value.toFixed(1)}`
}

export default function WeeklyReportView({ report }) {
  const { t, i18n } = useTranslation()

  if (!report) return null

  const findings = report.findings || {}
  const nutrition = findings.nutrition || {}
  const exercise = findings.exercise || {}
  const activity = findings.activity || {}
  const body = findings.body || {}
  const highlights = findings.highlights || []

  const locale = i18n.language === 'de' ? 'de-DE' : 'en-US'
  const startDate = report.start_date ? new Date(report.start_date).toLocaleDateString(locale) : ''
  const endDate = report.end_date ? new Date(report.end_date).toLocaleDateString(locale) : ''

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center pb-4 border-b">
        <h2 className="text-2xl font-bold text-gray-900">{t('reports.weekly.title')}</h2>
        <p className="text-gray-600 mt-1">
          {t('reports.weekly.period', { start: startDate, end: endDate })}
        </p>
        {report.health_score != null && (
          <div className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-primary-50 rounded-full">
            <StarIcon className="h-5 w-5 text-primary-600" />
            <span className="font-semibold text-primary-700">
              {t('reports.weekly.weekScore')}: {report.health_score}/100
            </span>
          </div>
        )}
      </div>

      {/* Highlights */}
      {highlights.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <h3 className="font-semibold text-yellow-800 mb-2 flex items-center gap-2">
            <StarIcon className="h-5 w-5" />
            {t('reports.weekly.highlights')}
          </h3>
          <ul className="space-y-1">
            {highlights.map((item, idx) => (
              <li key={idx} className="text-sm text-yellow-700 flex items-start gap-2">
                <span className="text-yellow-500 mt-0.5">&#8226;</span>
                {item}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Nutrition Section */}
      <div className={`rounded-lg border ${sectionColors.nutrition.border}`}>
        <div className={`${sectionColors.nutrition.header} px-4 py-3 rounded-t-lg flex items-center gap-2`}>
          <FireIcon className={`h-5 w-5 ${sectionColors.nutrition.icon}`} />
          <h3 className="font-semibold text-gray-900">{t('reports.weekly.nutrition')}</h3>
        </div>
        <div className={`${sectionColors.nutrition.bg} p-4 rounded-b-lg`}>
          {nutrition.days_on_target != null ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <MetricCard
                label={t('reports.weekly.daysOnTarget')}
                value={nutrition.days_on_target}
                unit="/7"
              />
              <MetricCard
                label={t('reports.weekly.avgCalories')}
                value={nutrition.avg_calories != null ? Math.round(nutrition.avg_calories) : null}
                unit={t('reports.weekly.kcal')}
                subtext={nutrition.target_calories ? t('reports.weekly.vsTarget', { target: nutrition.target_calories }) : null}
              />
              <MetricCard
                label={t('reports.weekly.avgProtein')}
                value={nutrition.avg_protein != null ? Math.round(nutrition.avg_protein) : null}
                unit={t('reports.weekly.grams')}
              />
              <MetricCard
                label={t('reports.weekly.avgFat')}
                value={nutrition.avg_fat != null ? Math.round(nutrition.avg_fat) : null}
                unit={t('reports.weekly.grams')}
              />
            </div>
          ) : (
            <p className="text-sm text-gray-500 text-center py-2">{t('reports.weekly.noData')}</p>
          )}
        </div>
      </div>

      {/* Exercise Section */}
      <div className={`rounded-lg border ${sectionColors.exercise.border}`}>
        <div className={`${sectionColors.exercise.header} px-4 py-3 rounded-t-lg flex items-center gap-2`}>
          <BoltIcon className={`h-5 w-5 ${sectionColors.exercise.icon}`} />
          <h3 className="font-semibold text-gray-900">{t('reports.weekly.exercise')}</h3>
        </div>
        <div className={`${sectionColors.exercise.bg} p-4 rounded-b-lg`}>
          {exercise.sessions_completed != null ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <MetricCard
                label={t('reports.weekly.sessionsCompleted')}
                value={exercise.sessions_completed}
              />
              <MetricCard
                label={t('reports.weekly.exercisesPerformed')}
                value={exercise.exercises_performed}
              />
              <MetricCard
                label={t('reports.weekly.newPRs')}
                value={exercise.new_prs}
              />
              <MetricCard
                label={t('reports.weekly.scoreChanges')}
                value={exercise.score_changes}
              />
            </div>
          ) : (
            <p className="text-sm text-gray-500 text-center py-2">{t('reports.weekly.noData')}</p>
          )}
        </div>
      </div>

      {/* Activity Section */}
      <div className={`rounded-lg border ${sectionColors.activity.border}`}>
        <div className={`${sectionColors.activity.header} px-4 py-3 rounded-t-lg flex items-center gap-2`}>
          <HeartIcon className={`h-5 w-5 ${sectionColors.activity.icon}`} />
          <h3 className="font-semibold text-gray-900">{t('reports.weekly.activity')}</h3>
        </div>
        <div className={`${sectionColors.activity.bg} p-4 rounded-b-lg`}>
          {activity.avg_steps != null ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <MetricCard
                label={t('reports.weekly.avgSteps')}
                value={activity.avg_steps != null ? Math.round(activity.avg_steps).toLocaleString() : null}
              />
              <MetricCard
                label={t('reports.weekly.avgSleepHours')}
                value={activity.avg_sleep_hours != null ? activity.avg_sleep_hours.toFixed(1) : null}
                unit={t('reports.weekly.hours')}
              />
              <MetricCard
                label={t('reports.weekly.avgRestingHR')}
                value={activity.avg_resting_hr != null ? Math.round(activity.avg_resting_hr) : null}
                unit={t('reports.weekly.bpm')}
              />
              <MetricCard
                label={t('reports.weekly.intensityMinutes')}
                value={activity.total_intensity_minutes != null ? Math.round(activity.total_intensity_minutes) : null}
                unit="min"
              />
            </div>
          ) : (
            <p className="text-sm text-gray-500 text-center py-2">{t('reports.weekly.noData')}</p>
          )}
        </div>
      </div>

      {/* Body Section */}
      <div className={`rounded-lg border ${sectionColors.body.border}`}>
        <div className={`${sectionColors.body.header} px-4 py-3 rounded-t-lg flex items-center gap-2`}>
          <ScaleIcon className={`h-5 w-5 ${sectionColors.body.icon}`} />
          <h3 className="font-semibold text-gray-900">{t('reports.weekly.body')}</h3>
        </div>
        <div className={`${sectionColors.body.bg} p-4 rounded-b-lg`}>
          {body.weight_change != null || body.body_fat_change != null ? (
            <div className="grid grid-cols-2 gap-4">
              <MetricCard
                label={t('reports.weekly.weightChange')}
                value={formatChange(body.weight_change)}
                unit={t('reports.weekly.kg')}
              />
              <MetricCard
                label={t('reports.weekly.bodyFatChange')}
                value={formatChange(body.body_fat_change)}
                unit={t('reports.weekly.percent')}
              />
            </div>
          ) : (
            <p className="text-sm text-gray-500 text-center py-2">{t('reports.weekly.noMeasurements')}</p>
          )}
        </div>
      </div>
    </div>
  )
}
